import React from "react";
import ReactPlayer from "react-player";
import CircularProgressRing from "./CircularProgressRing";

const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const StageIcon = ({ stroke = "#cbd5e1" }) => (
  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);


const VideoInterviewStage = ({
  videoSession,
  currentStage,
  currentStatus,
  tabLoading,
  isSharedReport,
  canAssignStage = true,
  isPlaying,
  setIsPlaying,
  formatDateOnly,
  setShowVideoModal,
  setModalType,
  initiatingVideoCall,
  proctoring,
  cheatingFrames,
  showAllCheatingImages,
  setShowAllCheatingImages,
  selectedProctorIndex,
  setSelectedProctorIndex,
  setOpenProctorLightbox,
  setSelectedProctorImage,
}) => {
  const isMobile = window.innerWidth < 767;

  if (tabLoading) {
    return (
      <div className="cpv2-loading" style={{ minHeight: 200 }}>
        <div className="spinner-border" />
      </div>
    );
  }

  const normalize = (v) => (v || "").trim().toLowerCase();
  const videoStatus = normalize(videoSession?.status);
  const safeCurrentStatus = normalize(currentStatus);

  const scheduleBtn = !isSharedReport && canAssignStage && (
    <button
      className="cpv2-video__btn cpv2-video__btn--primary"
      onClick={() => { setShowVideoModal(true); setModalType("video"); }}
      disabled={initiatingVideoCall}
    >
      {initiatingVideoCall ? "Initiating call..." : "Schedule a video interview"}
    </button>
  );

  const rescheduleBtn = !isSharedReport && canAssignStage && (
    <button
      className="cpv2-video__btn cpv2-video__btn--primary"
      onClick={() => { setShowVideoModal(true); setModalType("video"); }}
      disabled={initiatingVideoCall}
    >
      {initiatingVideoCall ? "Initiating call..." : "Schedule a video interview again"}
    </button>
  );

  const TranscriptMessages = () => (
    <>
      {videoSession?.transcripts?.map((msg, idx) => {
        if (!msg.content?.trim()) return null;
        const isAgent = msg.content_by === "AI";
        const speaker = isAgent ? "Agent voice" : msg.content_by;
        return (
          <div className="cpv2-video__transcript-row" key={idx}>
            {isAgent ? (
              <div className="agent-avatar">
                <img src="/images/candidate-example.jpeg" alt="avatar" />
              </div>
            ) : (
              <div className="avatar">
                {msg.content_by?.charAt(0).toUpperCase() || "U"}
              </div>
            )}
            <div>
              <p className="speaker">{speaker}</p>
              <p className="msg">{msg.content}</p>
            </div>
          </div>
        );
      })}
    </>
  );

  const isCompleted = videoStatus === "completed" && videoSession?.result;

  if (isCompleted) {
    const result = videoSession.result;
    const overallRating = result?.overall_rating || videoSession?.score || 0;
    const hasScores = result?.technical_score || result?.behavioral_score || result?.total_score;
    const skillCoverage = result?.skill_coverage ? Object.entries(result.skill_coverage) : [];
    const strengths = result?.strengths || [];
    const areasForImprovement = result?.areas_for_improvement || [];
    const completedOn = videoSession?.ended_at
      ? new Date(videoSession.ended_at).toLocaleString(undefined, { day: "numeric", month: "long", year: "numeric" }).replace(",", "")
      : null;
    const impression = result?.overall_impression || result?.technical_skills?.description || "";

    return (
      <div>
        <div className="cpv2-video">

          {/* ── Score header ── */}
          <div className="cpv2-video__score-row">
            {!isMobile && (
              <CircularProgressRing
                percentage={overallRating.toFixed(2)}
                label="Video score"
              />
            )}
            <div className="cpv2-video__score-info">
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
                {overallRating > 0 && (
                  <h5 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#1a2e4a" }}>
                    {parseFloat(overallRating).toFixed(2)}%
                  </h5>
                )}
                {completedOn && (
                  <span style={{ fontSize: 11, color: "#8a9199" }}>
                    Completed {completedOn}
                  </span>
                )}
              </div>
              {impression && (
                <p style={{ fontSize: 12, color: "#525b66", margin: 0, lineHeight: 1.6, maxWidth: 640 }}>
                  {impression}
                </p>
              )}
            </div>
          </div>

          {/* ── Score metrics ── */}
          {hasScores && (
            <div className="cpv2-video__metrics">
              {result?.technical_score != null && (
                <div className="cpv2-video__metric-item">
                  <span className="cpv2-video__metric-label">Technical</span>
                  <span className="cpv2-video__metric-value">
                    {result.technical_score.toFixed(2)}
                    <small>/80</small>
                  </span>
                </div>
              )}
              {result?.behavioral_score != null && (
                <div className="cpv2-video__metric-item">
                  <span className="cpv2-video__metric-label">Behavioral</span>
                  <span className="cpv2-video__metric-value">
                    {result.behavioral_score.toFixed(2)}
                    <small>/20</small>
                  </span>
                </div>
              )}
              {result?.total_score != null && (
                <div className="cpv2-video__metric-item cpv2-video__metric-item--total">
                  <span className="cpv2-video__metric-label">Total</span>
                  <span className="cpv2-video__metric-value">
                    {result.total_score.toFixed(2)}
                    <small>/100</small>
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Skill coverage ── */}
          {skillCoverage.length > 0 && (
            <div className="cpv2-video__section">
              <h6 className="cpv2-video__section-title">Skill Coverage</h6>
              <div className="cpv2-video__skill-chips">
                {skillCoverage.map(([skill, score], i) => (
                  <span key={i} className="cpv2-video__skill-chip">
                    {skill}
                    <strong>{score}/10</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Strengths & Areas ── */}
          {(strengths.length > 0 || areasForImprovement.length > 0) && (
            <div className="cpv2-video__insights-grid">
              {strengths.length > 0 && (
                <div className="cpv2-video__insight-card cpv2-video__insight-card--green">
                  <h6 className="cpv2-video__insight-title">Strengths</h6>
                  <ul className="cpv2-video__insight-list">
                    {strengths.map((item, i) => (
                      <li key={i}>
                        <CheckIcon />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {areasForImprovement.length > 0 && (
                <div className="cpv2-video__insight-card cpv2-video__insight-card--amber">
                  <h6 className="cpv2-video__insight-title">Areas for Improvement</h6>
                  <ul className="cpv2-video__insight-list">
                    {areasForImprovement.map((item, i) => (
                      <li key={i}>
                        <AlertIcon />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ── Recording & Transcript ── */}
          {(videoSession?.recording_link || videoSession?.transcripts?.length > 0) && (
            <div className="cpv2-video__section">
              <h6 className="cpv2-video__section-title">Recording &amp; Transcript</h6>
              <div className={`cpv2-video__two-col${isMobile ? " cpv2-video__two-col--stack" : ""}`}>
                <div className="cpv2-video__video-wrap">
                  <ReactPlayer
                    url={videoSession?.recording_link}
                    controls
                    playing={isPlaying}
                    width="100%"
                    height="100%"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    config={{ file: { attributes: { controlsList: "nodownload", preload: "metadata" } } }}
                  />
                </div>
                <div className="cpv2-video__transcript">
                  {videoSession?.transcripts?.length > 0
                    ? <TranscriptMessages />
                    : <p style={{ fontSize: 12, color: "#8a9199", padding: "12px 0" }}>No transcript available.</p>
                  }
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Proctoring ── */}
        {proctoring && cheatingFrames.length > 0 && (
          <div className="cpv2-video cpv2-video--proctor">
            <div className="cpv2-video__proctor-header">
              <h6 className="cpv2-video__section-title" style={{ marginBottom: 0 }}>Proctoring Analysis</h6>
              <span className="cpv2-video__proctor-count">
                {cheatingFrames.length} suspicious frame{cheatingFrames.length !== 1 ? "s" : ""} detected
              </span>
            </div>
            <div className="cpv2-video__proctor-grid">
              {(showAllCheatingImages ? cheatingFrames : cheatingFrames.slice(0, 8)).map((frame, index) => (
                <div
                  key={index}
                  className="cpv2-video__proctor-thumb"
                  onClick={() => {
                    setSelectedProctorIndex(index);
                    setSelectedProctorImage(frame.image_url);
                    setOpenProctorLightbox(true);
                  }}
                >
                  <img
                    src={frame.image_url}
                    alt=""
                    title={frame?.llm_eval?.summary || ""}
                  />
                </div>
              ))}
            </div>
            {cheatingFrames.length > 8 && (
              <button
                className="cpv2-video__proctor-toggle"
                onClick={() => setShowAllCheatingImages(!showAllCheatingImages)}
              >
                {showAllCheatingImages ? "View Less" : `View all ${cheatingFrames.length} frames`}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Non-completed states ──────────────────────────────────────────────

  if (!currentStage) {
    return (
      <div className="cpv2-video">
        <div className="cpv2-video__empty-state">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="23 7 16 12 23 17 23 7" />
            <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
          </svg>
          <p>No video interview scheduled yet</p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            {scheduleBtn}
          </div>
        </div>
      </div>
    );
  }

  if (safeCurrentStatus === "assigned") {
    return (
      <div className="cpv2-video">
        <div className="cpv2-video__status-card cpv2-video__status-card--info">
          <span className="cpv2-video__status-dot cpv2-video__status-dot--blue" />
          Video Interview assigned — waiting for candidate to complete
        </div>
      </div>
    );
  }

  if (safeCurrentStatus === "not scheduled") {
    return (
      <div className="cpv2-video">
        <div className="cpv2-video__status-card cpv2-video__status-card--info">
          <span className="cpv2-video__status-dot cpv2-video__status-dot--blue" />
          Video Interview assigned — yet to be scheduled
        </div>
      </div>
    );
  }

  if (safeCurrentStatus === "scheduled") {
    return (
      <div className="cpv2-video">
        <div className="cpv2-video__status-card cpv2-video__status-card--info">
          <span className="cpv2-video__status-dot cpv2-video__status-dot--yellow" />
          Video Interview sent. Due at {formatDateOnly(currentStage.expire_at)}
        </div>
      </div>
    );
  }

  if (safeCurrentStatus === "ongoing") {
    return (
      <div className="cpv2-video">
        <div className="cpv2-video__status-card cpv2-video__status-card--info">
          <span className="cpv2-video__status-dot cpv2-video__status-dot--green cpv2-video__status-dot--pulse" />
          Video Interview is in progress
        </div>
      </div>
    );
  }

  if (safeCurrentStatus === "expired") {
    return (
      <div className="cpv2-video">
        <div className="cpv2-stage-empty">
          <StageIcon stroke="#fcd34d" />
          <p className="cpv2-stage-empty__msg cpv2-stage-empty__msg--warning">Video call stage expired</p>
          {rescheduleBtn}
        </div>
      </div>
    );
  }

  if (["failed", "not completed", "revoked"].includes(safeCurrentStatus)) {
    return (
      <div className="cpv2-video">
        <div className="cpv2-stage-empty">
          <StageIcon stroke="#fca5a5" />
          <p className="cpv2-stage-empty__msg cpv2-stage-empty__msg--error">Video call was not completed by the candidate</p>
          {rescheduleBtn}
        </div>
      </div>
    );
  }

  if (safeCurrentStatus === "completed" && videoSession?.result) {
    const result = videoSession.result;
    const overallRating = result?.overall_rating || 0;
    return (
      <div className="cpv2-video">
        <div className="cpv2-video__score-row">
          <CircularProgressRing percentage={overallRating} label="Video score" />
          <div className="cpv2-video__score-info">
            <h5 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#1a2e4a" }}>Overall Score</h5>
            <p style={{ margin: 0, fontSize: 12, color: "#525b66" }}>
              {result?.technical_skills?.description}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (safeCurrentStatus === "completed" && !videoSession?.result) {
    return (
      <div className="cpv2-video">
        <div className="cpv2-video__status-card cpv2-video__status-card--info">
          <span className="cpv2-video__status-dot cpv2-video__status-dot--yellow" />
          Video interview completed — results processing
        </div>
      </div>
    );
  }

  return (
    <div className="cpv2-video">
      <div className="cpv2-video__status-card cpv2-video__status-card--info">
        <span className="cpv2-video__status-dot cpv2-video__status-dot--blue" />
        Status pending
      </div>
    </div>
  );
};

export default VideoInterviewStage;
